//Class that designed to help with adding blocks and provide visual guidelines

import { intersect, distance } from 'mathjs';
import {COLORS, FONTS} from '../Constants'

const kDebug = false;

const kDirectionNone = -1;
const kDirectionUp = 0;
const kDirectionInsideDown = 1;

const kSnappingThreshold = 10;
const kDefaultWidth = 400;

export default class Adder {
    constructor(view){
        this.view = view;
        this.centerPoint = {x:0, y:0}
        this.relative = { width: false, height: false};
        this.boundingRect = {
            x:0,
            y:0,
            width: kDefaultWidth, 
            height: 25
        }

        this.rects = {};
        this.parentBlock = null;

        this.blockSectionMode = false; //TODO: need to move this in to subclass 
        this.blockSectionData = null;
    }
    
    update(canvasRect){
        for(let block of this.view.blocks){
            if(block.boundingRect.width === 0 && block.boundingRect.height === 0){
                continue;
            }
            this.rects[ block.id ] = block.boundingRect;
            this.rects[ block.id ].block = block;
        }

        this.parentBlock = this.view.getBlockAt(this.centerPoint.x, this.centerPoint.y);
        if(this.parentBlock && 
            this.parentBlock.type !== 'Block' && 
            this.parentBlock.type !== 'Image' &&
            this.parentBlock.type !== 'Background'){
            this.parentBlock = null;
        }
        if(this.blockSectionMode){
            this.parentBlock = null;
        }

        let castDirection;
        if(this.parentBlock){
            if(this.parentBlock.children.length === 0){
                castDirection = kDirectionInsideDown
            }
            else{
                castDirection = kDirectionNone;
            }
        }
        else{
            castDirection = kDirectionUp;
        }
        

    }
    setDefaultSize(size){
        this.boundingRect.width = size.width;
        this.boundingRect.height = size.height;
    }

    getClosestSectionHeight( targetY ){
        let delta = Number.MAX_SAFE_INTEGER;
        let out = 0;

        for(let block of this.view.blocks){
            let rect = block.worldRenderBRect;
            if(Math.abs(targetY - rect.y) < delta ){
                delta = targetY - rect.y;
                out = rect.y;
            }
        }

        //special case with last block 
        if(this.view.blocks.length !== 0){
            let block = this.view.blocks[ this.view.blocks.length - 1];
            let bottom = block.worldRenderBRect.y + block.worldRenderBRect.height;
            if(Math.abs(targetY - bottom) < delta){
                out = bottom;
            }   
        }

        return out;
    }

    setCenterPoint(point){
        this.centerPoint = point;
        if(this.relative.width){
            let width = this.view.canvasRect.width * (this.boundingRect.width/100.0);
            this.boundingRect.x = this.view.canvasRect.width - width;
        }
        else{
            this.boundingRect.x = point.x - this.boundingRect.width*0.5;
        }

        this.boundingRect.y = point.y - this.boundingRect.height*0.5;

        if(this.blockSectionMode){
            this.boundingRect.y = this.getClosestSectionHeight( this.centerPoint.y );
        }
    }

    createBlock( creationData ){
        //Moving Section Blocks down in case of Section Mode.
        if(this.blockSectionMode){
            this.view.overlay.moveSectionBlocks( this.boundingRect.y, this.boundingRect.height);
        }

        let block = null;
        if(this.blockSectionData){
            //Unpack Section Block
            block = this.view.createBlock( this.blockSectionData.type,  this.blockSectionData.id);
            block.unpack(this.blockSectionData);

            //Unpack Connections
            block.unpackConnections(this.blockSectionData);
            
            this.view.blocks.push( block );

            //Move block to creation point 
            block.boundingRect.y = this.boundingRect.y  / this.view.state.zoom;

            block.update();

            this.blockSectionData = null;
        }
        else{
            block = this.view.createBlock( creationData.blockType );
            block.initDefault();
    
            let width = this.boundingRect.width;
            if(this.relative.width){
                width = 1024 * (this.boundingRect.width/100.0); //1024 is fixed width for design resolution;
            }
    
            block.boundingRect = {
                x: this.boundingRect.x,
                y: this.boundingRect.y, 
                width: width, 
                height: this.boundingRect.height
            }
    
    
            if(!this.parentBlock){
                this.view.blocks.push( block );
            }
            else{
                let point = this.parentBlock.toLocalRender({x: this.boundingRect.x, y: this.boundingRect.y });
                block.boundingRect.x = point.x;
                block.boundingRect.y = point.y;
                this.parentBlock.addChild( block );
            }
            block.update();  
        }        

        this.view.overlay.onBlockAdded( block );
        
        return block;
    }

    intersectRect( line, rect, block, direction){
        let r = intersect(
            [line[0].x, line[0].y],
            [line[1].x, line[1].y],
            [rect.x, rect.y+rect.height],
            [rect.x+rect.width, rect.y+rect.height]);
        
        if(r[0] > rect.x && r[0] < rect.x+rect.width && r[1] > line[0].y && r[1] < line[1].y){
            let d = distance([this.centerPoint.x, this.centerPoint.y], r);

            return {
                rect : rect,
                block : block,
                point : {x:r[0], y:r[1]},
                direction: direction,
                distance: d - this.boundingRect.height*0.5,
            }
        }
        return null;
    }

    castRay( parent, direction, canvasRect){
        let info = null;
        let dMin = 10000000;

        switch(direction){
            case kDirectionInsideDown:
                info = this.intersectRect(
                    [{x:this.centerPoint.x, y:this.centerPoint.y},
                    {x:this.centerPoint.x, y:canvasRect.height}],
                    parent.boundingRect, 
                    parent,
                    kDirectionInsideDown
                );

                break;

            case kDirectionUp:
                for (let [, rect] of Object.entries(this.rects)){
                    let r = intersect(
                        [this.centerPoint.x, this.centerPoint.y],
                        [this.centerPoint.x, 0],
                        [rect.x, rect.y+rect.height],
                        [rect.x+rect.width, rect.y+rect.height]);

                    if(r[0] > rect.x && r[0] < rect.x+rect.width && this.centerPoint.y > rect.y+rect.height){

                        let d = distance([this.centerPoint.x, this.centerPoint.y], r);
                        if(d < dMin){
                            dMin = d
                            info = {
                                rect : rect,
                                block : rect.block,
                                point : {x:r[0], y:r[1]},
                                direction: kDirectionUp,
                                distance: d - this.boundingRect.height*0.5,

                            }
                        }

                    }
                }
                break;
            default:
                break;
        }
        return info;
    }

    renderOverlay(ctx, creationData){
        ctx.strokeStyle = 'rgb(200, 200, 0, 1.0)';
        
        let width = this.boundingRect.width;
        if(this.relative.width){
            width = this.view.canvasRect.width * (this.boundingRect.width/100.0)
        }
        
        let rect = {
            x: this.boundingRect.x,
            y: this.boundingRect.y, 
            width: width, 
            height: this.boundingRect.height
        }

        if(this.blockSectionMode){
            rect.height = 1;
        }

        ctx.strokeRect(
            rect.x,
            rect.y,
            rect.width,
            rect.height);

        ctx.font = FONTS.anchorLabel;
        ctx.fillStyle = COLORS.anchorGuideText;
        let text = '';
        if(this.parentBlock){
            text = 'Add '+creationData.blockType + ' inside ' + this.parentBlock.type;
        }
        else{
            text = 'Add '+creationData.blockType;
        }
        ctx.fillText(text,
             rect.x + 10,
             rect.y + rect.height + 15);

        //DEBUG
        if(kDebug){
            for (let [, rect] of Object.entries(this.rects)){
                ctx.strokeStyle = 'rgb(255, 0, 255, 1.0)';
                ctx.strokeRect(
                    rect.x,
                    rect.y,
                    rect.width,
                    rect.height);
            }
        }
    }
}
